// src/pages/Lounge/Clubs/ClubHome/ClubHome.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import TextPostCard from "@/components/Post/TextPostCard/TextPostCard";
import PhotoPostCard from "@/components/Post/PhotoPostCard/PhotoPostCard";
import VideoPostCard from "@/components/Post/VideoPostCard/VideoPostCard";
import api from "@/utils/axios";
import cls from "./ClubHome.module.scss";
import Loading from "@/components/Loading/Loading";

import avatarPng from "@/assets/UI/avatar.png";

const ALLOWED_STATES = new Set([
  "activeClubMember",
  "activeClubHost",
  "activeClubPartner",
]);

const LANGS = ["en", "si", "ta", "hi"];

const safeStr = (v) => (v == null ? "" : String(v));
const hasText = (v) => safeStr(v).trim().length > 0;

const extFromFile = (file) => {
  const name = safeStr(file?.name);
  const dot = name.lastIndexOf(".");
  if (dot === -1) return "";
  return name.slice(dot + 1).toLowerCase();
};

const getVideoIdFromPost = (post) => {
  const v = post?.videoId;
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && v?._id) return v._id;
  return "";
};

/** Same helper idea as VideoUpload.jsx: read intrinsic dimensions before presign */
async function sniffVideoMeta(file) {
  return new Promise((resolve) => {
    try {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.muted = true;
      el.playsInline = true;

      el.onloadedmetadata = () => {
        URL.revokeObjectURL(el.src);
        const width = el.videoWidth || 0;
        const height = el.videoHeight || 0;
        const orientation =
          width && height && width < height ? "portrait" : "landscape";
        resolve({ width, height, orientation });
      };

      el.onerror = () => {
        try {
          URL.revokeObjectURL(el.src || "");
        } catch {
          // Intentionally ignored: this best-effort cleanup should not block the UI.
        }
        resolve({ width: 0, height: 0, orientation: "landscape" });
      };

      el.src = URL.createObjectURL(file);
    } catch {
      resolve({ width: 0, height: 0, orientation: "landscape" });
    }
  });
}

const ClubHome = () => {
  const { t } = useTranslation();
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // keep top tabs row exactly like MemberProfileOverView pattern
  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  // feed
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedErr, setFeedErr] = useState("");
  const [posts, setPosts] = useState([]);

  // upload progress
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadLabel, setUploadLabel] = useState("");

  // composer
  const [composerOpen, setComposerOpen] = useState(false);
  const [mode, setMode] = useState(""); // "" | "text" | "photo" | "video"

  const [lang, setLang] = useState("en");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [images, setImages] = useState([]); // File[]
  const [videoFile, setVideoFile] = useState(null); // File|null

  // preview-only
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [formOk, setFormOk] = useState("");

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const { data } = await api.get(`/v1/lounge/clubs/${clubId}`);
        if (ignore) return;

        const memberState = safeStr(data?.memberState);
        if (!ALLOWED_STATES.has(memberState)) {
          navigate(`/lounge/clubs/single/${clubId}`, { replace: true });
          return;
        }

        setPayload(data || null);
      } catch (err) {
        if (ignore) return;
        setError(
          err?.response?.data?.message ||
            t("clubHome.states.error", "Something went wrong."),
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [clubId, navigate, t]);

  const loadFeed = async () => {
    setFeedLoading(true);
    setFeedErr("");
    try {
      const res = await api.get(`/v1/posts/feeds/clubs/${clubId}/published`, {
        params: { page: 1, limit: 15 },
      });

      const rows =
        res?.data?.rows ||
        res?.data?.posts ||
        res?.data?.data ||
        res?.data?.items ||
        [];

      setPosts(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setFeedErr(
        e?.response?.data?.message || e?.message || "Failed to load feed",
      );
      setPosts([]);
    } finally {
      setFeedLoading(false);
    }
  };

  useEffect(() => {
    loadFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  // cleanup previews
  useEffect(() => {
    return () => {
      try {
        imagePreviewUrls.forEach((u) => u && URL.revokeObjectURL(u));
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
      try {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const club = payload?.club || null;
  const memberState = safeStr(payload?.memberState);

  const clubName =
    safeStr(club?.clubName) || t("clubHome.header.clubName", "Club");

  const clubNo = club?.clubNo != null ? String(club.clubNo) : "";
  const streetName = safeStr(club?.streetName);
  const cityName = safeStr(club?.cityName);

  const clubAddressParts = [
    hasText(clubNo) ? `${t("clubHome.header.clubNo", "Club")} ${clubNo}` : "",
    streetName,
    cityName,
  ].filter((x) => hasText(x));

  const clubAddress = clubAddressParts.length
    ? clubAddressParts.join(" · ")
    : t("clubHome.header.addressFallback", "Address not available");

  const clubAvatarSrc =
    club?.clubAvatarThumb?.url || club?.clubAvatar?.url || avatarPng;

  const showHostDash = memberState === "activeClubHost";
  const showPartnerDash = memberState === "activeClubPartner";

  const dashMeta = useMemo(() => {
    if (showHostDash) {
      return {
        label: t("clubHome.header.hostDashboard", "Host Dashboard"),
        to: `/lounge/clubs/single/${clubId}/host-dashboard`,
      };
    }
    if (showPartnerDash) {
      return {
        label: t("clubHome.header.partnerDashboard", "Partner Dashboard"),
        to: `/lounge/clubs/single/${clubId}/partner-dashboard`,
      };
    }
    return null;
  }, [clubId, showHostDash, showPartnerDash, t]);

  // second tabs row (club sections)
  const clubBase = `/lounge/clubs/single/${clubId}`;
  const clubTabs = [
    {
      key: "feed",
      label: t("clubHome.tabs.feed", "Feed"),
      to: `${clubBase}/home`,
    },
    {
      key: "members",
      label: t("clubHome.tabs.members", "Members"),
      to: `${clubBase}/members`,
    },
    {
      key: "events",
      label: t("clubHome.tabs.events", "Events"),
      to: `${clubBase}/events`,
    },
    {
      key: "shop",
      label: t("clubHome.tabs.shop", "Shop"),
      to: `/lounge/marketplace/${clubId}`,
    },
  ];

  const isActiveTab = (to) => {
    const p = safeStr(location.pathname);
    if (p === to) return true;
    if (
      to.endsWith("/home") &&
      (p === clubBase || p.startsWith(`${clubBase}/home`))
    )
      return true;
    return p.startsWith(to);
  };

  // ---- composer helpers (same class names as MemberFriendsFeed) ----
  const clearPreviews = () => {
    setImagePreviewUrls((prev) => {
      prev.forEach((u) => {
        try {
          if (u) URL.revokeObjectURL(u);
        } catch {
          // Intentionally ignored: this best-effort cleanup should not block the UI.
        }
      });
      return [];
    });

    setVideoPreviewUrl((prev) => {
      try {
        if (prev) URL.revokeObjectURL(prev);
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
      return "";
    });
  };

  const resetForm = () => {
    setLang("en");
    setTitle("");
    setDescription("");
    setImages([]);
    setVideoFile(null);
    setFormErr("");
    setFormOk("");
    setUploadPct(0);
    setUploadLabel("");
    clearPreviews();
  };

  const closeComposer = () => {
    setComposerOpen(false);
    setMode("");
    resetForm();
  };

  const openComposer = (nextMode) => {
    setComposerOpen(true);
    setMode(nextMode);
    setFormErr("");
    setFormOk("");
    setImages([]);
    setVideoFile(null);
    setUploadPct(0);
    setUploadLabel("");
    clearPreviews();
  };

  const onPickImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remain = Math.max(0, 20 - images.length);
    const toAdd = files.slice(0, remain);

    setImages((prev) => [...prev, ...toAdd].slice(0, 20));
    setImagePreviewUrls((prev) =>
      [...prev, ...toAdd.map((f) => URL.createObjectURL(f))].slice(0, 20),
    );

    e.target.value = "";
  };

  const removeImageAt = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));

    setImagePreviewUrls((prev) => {
      const url = prev[idx];
      try {
        if (url) URL.revokeObjectURL(url);
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const onPickVideo = (e) => {
    const file = e.target.files?.[0] || null;

    setVideoPreviewUrl((prev) => {
      try {
        if (prev) URL.revokeObjectURL(prev);
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
      return file ? URL.createObjectURL(file) : "";
    });

    setVideoFile(file);
    e.target.value = "";
  };

  const validateClient = () => {
    setFormErr("");
    setFormOk("");

    if (!LANGS.includes(lang)) {
      setFormErr(
        t("clubHome.create.errors.lang", "Please select a valid language."),
      );
      return false;
    }

    if (mode === "text") {
      const okText = hasText(title) || hasText(description);
      if (!okText) {
        setFormErr(
          t(
            "clubHome.create.errors.textRequired",
            "Write a title or description.",
          ),
        );
        return false;
      }
    }

    if (mode === "photo") {
      if (images.length < 1) {
        setFormErr(
          t("clubHome.create.errors.photoRequired", "Add at least one photo."),
        );
        return false;
      }
      if (images.length > 20) {
        setFormErr(
          t("clubHome.create.errors.photoMax", "Max 20 photos per post."),
        );
        return false;
      }
    }

    if (mode === "video") {
      if (!videoFile) {
        setFormErr(
          t("clubHome.create.errors.videoRequired", "Please choose a video."),
        );
        return false;
      }
    }

    return true;
  };

  const presignImagePoster = async (file) => {
    const ext = extFromFile(file) || "jpg";
    const contentType = file?.type || "image/jpeg";
    const res = await api.post("/v1/posts/media/presign-image-poster", {
      ext,
      contentType,
    });
    return res.data; // expects: { uploadUrl, processedKey }
  };

  /**
   * Same as MemberFriendsFeed:
   * 1) sniff clientMeta
   * 2) POST /v1/videos/presign
   */
  const presignVideoViaVideos = async (file) => {
    const contentType = file?.type || "application/octet-stream";
    const filename =
      safeStr(file?.name) || `video.${extFromFile(file) || "mp4"}`;

    const clientMeta = (await sniffVideoMeta(file).catch(() => null)) || {
      width: 0,
      height: 0,
      orientation: "landscape",
    };

    const { data } = await api.post("/v1/videos/presign", {
      contentType,
      filename,
      clientMeta,
    });

    return data; // expects: { id, url, contentType, ... }
  };

  const putToS3WithProgress = (uploadUrl, file, onPct, contentTypeOverride) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);

      xhr.setRequestHeader(
        "Content-Type",
        contentTypeOverride || file?.type || "application/octet-stream",
      );

      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        onPct?.(pct);
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        reject(new Error(`Upload failed (${xhr.status})`));
      };

      xhr.onerror = () => reject(new Error("Upload failed (network error)"));
      xhr.send(file);
    });
  };

  const buildVideoMetaForLocalize = () => {
    const blank = {
      en: { title: "", subtitle: "" },
      si: { title: "", subtitle: "" },
      ta: { title: "", subtitle: "" },
      hi: { title: "", subtitle: "" },
    };

    blank[lang] = {
      title: hasText(title) ? title.trim() : "",
      subtitle: hasText(description) ? description.trim() : "",
    };

    return blank;
  };

  const submitPost = async () => {
    if (!validateClient()) return;

    setSubmitting(true);
    setFormErr("");
    setFormOk("");
    setUploadPct(0);
    setUploadLabel("");

    try {
      const baseBody = {
        visibility: "club",
        clubId, // ✅ required
        postLanguage: lang,
        title: hasText(title) ? title.trim() : null,
        description: hasText(description) ? description.trim() : null,
        publishMode: "now",
        postStatus: "pending", // ✅ club posts should be pending
      };

      if (mode === "text") {
        setUploadLabel(t("clubHome.create.publishing", "Publishing..."));
        await api.post("/v1/posts", { ...baseBody, type: "text-only" });
      }

      if (mode === "photo") {
        const uploaded = [];

        for (let i = 0; i < images.length; i++) {
          const f = images[i];

          setUploadLabel(
            t("clubHome.create.uploadingPhoto", "Uploading photo {{c}}/{{t}}", {
              c: i + 1,
              t: images.length,
            }),
          );
          setUploadPct(0);

          const pres = await presignImagePoster(f);
          await putToS3WithProgress(pres.uploadUrl, f, setUploadPct);

          uploaded.push({ key: pres.processedKey });
        }

        setUploadLabel(t("clubHome.create.publishing", "Publishing..."));
        setUploadPct(100);

        await api.post("/v1/posts", {
          ...baseBody,
          type: "photo-post",
          images: uploaded,
        });
      }

      if (mode === "video") {
        setUploadLabel(
          t("clubHome.create.uploadingVideo", "Uploading video..."),
        );
        setUploadPct(0);

        const presign = await presignVideoViaVideos(videoFile);

        const videoId = safeStr(presign?.id).trim();
        if (!videoId) {
          console.log("videos/presign response:", presign);
          throw new Error("Server did not return a video id for this upload.");
        }

        await putToS3WithProgress(
          presign.url,
          videoFile,
          setUploadPct,
          presign.contentType || videoFile?.type,
        );

        const meta = buildVideoMetaForLocalize();
        await api.post(`/v1/videos/${videoId}/localize`, { meta });
        await api.post(`/v1/videos/${videoId}/submit`);

        setUploadLabel(t("clubHome.create.publishing", "Publishing..."));
        setUploadPct(100);

        await api.post("/v1/posts", {
          ...baseBody,
          type: "video-post",
          videoId,
        });
      }

      setFormOk(t("clubHome.create.success", "Post submitted for approval."));
      resetForm();
      closeComposer();
    } catch (e) {
      setFormErr(e?.response?.data?.message || e?.message || "Failed");
    } finally {
      setSubmitting(false);
      setUploadLabel("");
      setUploadPct(0);
    }
  };

  const publishBtnText = useMemo(() => {
    if (!submitting) return t("clubHome.create.publish", "Publish");
    if (!uploadLabel) return t("clubHome.create.publishing", "Publishing...");
    if (uploadPct > 0 && uploadPct < 100)
      return `${uploadLabel} (${uploadPct}%)`;
    if (uploadPct === 100) return `${uploadLabel} (100%)`;
    return uploadLabel;
  }, [submitting, uploadLabel, uploadPct, t]);

  const renderFeedPost = (post) => {
    const type = safeStr(post?.type).toLowerCase();
    const hasImages = Array.isArray(post?.images) && post.images.length > 0;
    const hasVideo = !!getVideoIdFromPost(post);

    if (type === "video-post" || type === "video")
      return <VideoPostCard post={post} />;
    if (type === "photo-post" || type === "photo")
      return <PhotoPostCard post={post} />;
    if (type === "text-only" || type === "text")
      return <TextPostCard post={post} />;

    if (hasVideo) return <VideoPostCard post={post} />;
    if (hasImages) return <PhotoPostCard post={post} />;
    return <TextPostCard post={post} />;
  };

  // ✅ Loader gate AFTER hooks (same as SearchClubs)
  if (loading && !payload && !error) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }
  return (
    <div className={cls.page}>
      {/* Tabs row (keep as-is) */}
      <nav
        className={cls.tabs}
        aria-label={t("clubHome.topTabs.aria", "Lounge")}
      >
        <Link
          to="/lounge"
          className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.public", "Public")}
        </Link>
        <Link
          to="/lounge/friends-feed"
          className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.friends", "Friends")}
        </Link>
        <Link
          to="/lounge/clubs-feed"
          className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.clubs", "Clubs")}
        </Link>
        <Link
          to="/lounge/chat"
          className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.chat", "Chat")}
        </Link>
        <Link
          to="/lounge/search"
          className={`${cls.tabButton} ${isSearch ? cls.activeTab : ""}`}
        >
          🔍 {t("clubHome.topTabs.search", "Search")}
        </Link>
      </nav>

      {/* Header bar (avatar + name + actions) */}
      <header className={cls.header}>
        <div className={cls.headerMain}>
          <div className={cls.avatarWrap}>
            <img className={cls.avatar} src={clubAvatarSrc} alt={clubName} />
            <span className={cls.badgeOnline}>
              {t("clubHome.header.active", "Active")}
            </span>
          </div>

          <div className={cls.headerInfo}>
            <div className={cls.displayName}>{clubName}</div>

            <div className={cls.usernameRow}>
              <span className={cls.memberSince}>{clubAddress}</span>
            </div>

            {/* Meta row (replaces meta pills) */}
            <div className={cls.metaRow}>
              {dashMeta ? (
                <Link className={cls.btnPrimary} to={dashMeta.to}>
                  {dashMeta.label}
                </Link>
              ) : null}
            </div>

            {/* simple state row for loading/error */}
            {loading ? (
              <div className={cls.stateRow}>
                <span className={cls.stateDot} />
                <span className={cls.stateText}>
                  {t("clubHome.states.loading", "Loading club...")}
                </span>
              </div>
            ) : null}

            {!loading && error ? (
              <div className={cls.stateRowError}>
                <span className={cls.stateDot} />
                <span className={cls.stateText}>{error}</span>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {/* Tabs row (club sections) */}
      <nav
        className={cls.tabs}
        aria-label={t("clubHome.clubTabs.aria", "Club")}
      >
        {clubTabs.map((tab) => {
          const active = isActiveTab(tab.to);
          return (
            <Link
              key={tab.key}
              to={tab.to}
              className={`${cls.tabButton} ${active ? cls.activeTab : ""}`}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>

      {/* Center-only content (no left column) */}
      <div className={cls.centerCol}>
        {/* ✅ Replaced placeholder card with Club post composer (same classes as MemberFriendsFeed) */}
        <section className={cls.card}>
          <div className={cls.createHeader}>
            <img
              src={clubAvatarSrc}
              alt={t("clubHome.create.avatarAlt", "Club avatar")}
              className={cls.createPostAvatar}
            />
            <div className={cls.createHeaderText}>
              <div className={cls.createTitle}>
                {t("clubHome.create.title", "Create a club post")}
              </div>
              <div className={cls.createSub}>
                {t(
                  "clubHome.create.sub",
                  "Visible to this club. Submits as pending.",
                )}
              </div>
            </div>
          </div>

          <div className={cls.modeRow}>
            <button
              type="button"
              className={`${cls.modeBtn} ${
                mode === "text" ? cls.modeBtnActive : ""
              }`}
              onClick={() => openComposer("text")}
              disabled={submitting}
            >
              ✍️ {t("clubHome.create.modes.text", "Text")}
            </button>

            <button
              type="button"
              className={`${cls.modeBtn} ${
                mode === "photo" ? cls.modeBtnActive : ""
              }`}
              onClick={() => openComposer("photo")}
              disabled={submitting}
            >
              🖼️ {t("clubHome.create.modes.photo", "Photo")}
            </button>

            <button
              type="button"
              className={`${cls.modeBtn} ${
                mode === "video" ? cls.modeBtnActive : ""
              }`}
              onClick={() => openComposer("video")}
              disabled={submitting}
            >
              🎥 {t("clubHome.create.modes.video", "Video")}
            </button>
          </div>

          {composerOpen ? (
            <div className={cls.form}>
              <div className={cls.formRow}>
                <label className={cls.label}>
                  {t("clubHome.create.language", "Language")}
                </label>
                <select
                  className={cls.select}
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  disabled={submitting}
                >
                  {LANGS.map((l) => (
                    <option key={l} value={l}>
                      {t(`clubHome.create.langs.${l}`, l)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.formRow}>
                <label className={cls.label}>
                  {t("clubHome.create.titleLabel", "Title")}
                </label>
                <input
                  className={cls.input}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t(
                    "clubHome.create.titlePh",
                    "Add a short title (optional)",
                  )}
                  maxLength={160}
                  disabled={submitting}
                />
              </div>

              <div className={cls.formRow}>
                <label className={cls.label}>
                  {t("clubHome.create.descLabel", "Description")}
                </label>
                <textarea
                  className={cls.textarea}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t(
                    "clubHome.create.descPh",
                    "Write something to the club...",
                  )}
                  maxLength={4000}
                  rows={4}
                  disabled={submitting}
                />
              </div>

              {mode === "photo" ? (
                <div className={cls.formRow}>
                  <div className={cls.uploadRow}>
                    <div className={cls.uploadMeta}>
                      <div className={cls.uploadTitle}>
                        {t("clubHome.create.photosTitle", "Photos")}
                      </div>
                      <div className={cls.uploadHint}>
                        {t("clubHome.create.photosHint", "Up to 20 images")}
                      </div>
                    </div>

                    <label className={cls.fileBtn}>
                      {t("clubHome.create.addPhotos", "Add photos")}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={onPickImages}
                        disabled={submitting || images.length >= 20}
                        className={cls.fileInput}
                      />
                    </label>
                  </div>

                  {imagePreviewUrls.length ? (
                    <div className={cls.previewGrid}>
                      {imagePreviewUrls.map((url, idx) => (
                        <div key={url} className={cls.previewItem}>
                          <img
                            src={url}
                            alt={t(
                              "clubHome.create.previewPhotoAlt",
                              "Selected photo",
                            )}
                            className={cls.previewImg}
                          />
                          <button
                            type="button"
                            className={cls.previewRemove}
                            onClick={() => removeImageAt(idx)}
                            disabled={submitting}
                            aria-label={t("clubHome.create.remove", "Remove")}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {mode === "video" ? (
                <div className={cls.formRow}>
                  <div className={cls.uploadRow}>
                    <div className={cls.uploadMeta}>
                      <div className={cls.uploadTitle}>
                        {t("clubHome.create.videoTitle", "Video")}
                      </div>
                      <div className={cls.uploadHint}>
                        {t(
                          "clubHome.create.videoHint",
                          "Choose one video file",
                        )}
                      </div>
                    </div>

                    <label className={cls.fileBtn}>
                      {t("clubHome.create.addVideo", "Add video")}
                      <input
                        type="file"
                        accept="video/*"
                        onChange={onPickVideo}
                        disabled={submitting}
                        className={cls.fileInput}
                      />
                    </label>
                  </div>

                  {videoFile ? (
                    <div className={cls.videoPicked}>
                      <span className={cls.videoName} title={videoFile.name}>
                        {videoFile.name}
                      </span>
                      <button
                        type="button"
                        className={cls.thumbRemove}
                        onClick={() => {
                          setVideoFile(null);
                          setVideoPreviewUrl((prev) => {
                            try {
                              if (prev) URL.revokeObjectURL(prev);
                            } catch {
                              // Intentionally ignored: this best-effort cleanup should not block the UI.
                            }
                            return "";
                          });
                        }}
                        disabled={submitting}
                      >
                        ✕
                      </button>
                    </div>
                  ) : null}

                  {videoPreviewUrl ? (
                    <div className={cls.videoPreview}>
                      <video
                        className={cls.videoEl}
                        src={videoPreviewUrl}
                        controls
                        playsInline
                        preload="metadata"
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {formErr ? <div className={cls.formErr}>{formErr}</div> : null}
              {formOk ? <div className={cls.formOk}>{formOk}</div> : null}

              <div className={cls.actionsRow}>
                <button
                  type="button"
                  className={cls.publishBtn}
                  onClick={submitPost}
                  disabled={submitting}
                >
                  {publishBtnText}
                </button>

                <button
                  type="button"
                  className={cls.resetBtn}
                  onClick={resetForm}
                  disabled={submitting}
                >
                  {t("clubHome.create.reset", "Reset")}
                </button>

                <button
                  type="button"
                  className={cls.cancelBtn}
                  onClick={closeComposer}
                  disabled={submitting}
                >
                  {t("clubHome.create.cancel", "Cancel")}
                </button>
              </div>

              {mode === "video" ? (
                <div className={cls.noteMini}>
                  {t(
                    "clubHome.create.videoNote",
                    "Tip: processing can take a moment after upload.",
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
        <section className={cls.feedWrap}>
          {feedErr ? (
            <div className={cls.feedStateBad}>{feedErr}</div>
          ) : feedLoading ? (
            <div className={cls.feedState}>
              {t("clubHome.feed.loading", "Loading posts...")}
            </div>
          ) : posts.length === 0 ? (
            <div className={cls.feedState}>
              {t("clubHome.feed.empty", "No club posts yet.")}
            </div>
          ) : (
            <div className={cls.postList}>
              {posts.map((post) => (
                <React.Fragment key={post._id}>
                  {renderFeedPost(post)}
                </React.Fragment>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ClubHome;
